// src/services/planPerksService.js
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { LEGACY_CONSULTATIONS } from '../config/plans';

const NO_PERKS = { consultations: 0, teacherCorrections: 0 };

const toDate = (value) => (value?.toDate ? value.toDate() : value ? new Date(value) : null);

/**
 * Teacher time a user has bought and can still use: Zoom consultations and
 * hand-corrected texts. Summed over every subscription that is still running.
 *
 * Read from subscriptions/, not the user doc: subscriptions/ is written only by
 * the Stripe webhook and the admin dashboard, while the user doc has been reset
 * to 'free' by ordinary product flows before (see hasLiveSubscription in
 * functions/src/index.ts).
 *
 * Since Oct 2026 the webhook stamps `perks` on every subscription (Premium
 * gets 5 consultations and 10 corrections, 3 months gets none). Older
 * subscriptions have no `perks` field and keep what they were sold: monthly 1
 * consultation, quarterly 3.
 *
 * These are totals, not what is left — callers subtract what was used since
 * `since` (the start of the earliest running subscription), so bookings from
 * an earlier, expired plan don't eat into a new one.
 */
export async function getActivePerks(userId) {
  if (!userId) return { ...NO_PERKS, since: null };
  const now = new Date();
  const totals = { ...NO_PERKS, since: null };
  let foundActive = false;

  const snap = await getDocs(query(collection(db, 'subscriptions'), where('userId', '==', userId)));
  snap.docs.forEach((subDoc) => {
    const sub = subDoc.data();
    // Same rule as subscriptionService.getSubscriptionStatus: 'expired' and
    // 'inactive' end access at once, 'cancelled' runs to its end date.
    const status = String(sub.status || '').toLowerCase();
    if (['expired', 'inactive'].includes(status)) return;
    const end = toDate(sub.endDate);
    if (!end || end <= now) return;
    foundActive = true;
    const start = toDate(sub.startDate);
    if (start && (!totals.since || start < totals.since)) totals.since = start;
    if (sub.perks) {
      totals.consultations += Number(sub.perks.consultations) || 0;
      totals.teacherCorrections += Number(sub.perks.teacherCorrections) || 0;
    } else {
      totals.consultations += LEGACY_CONSULTATIONS[sub.planId] || 0;
    }
  });
  if (foundActive) return totals;

  // Very old purchases may exist only on the user doc.
  const userSnap = await getDoc(doc(db, 'users', userId));
  const user = userSnap.exists() ? userSnap.data() : {};
  const end = toDate(user.subscriptionEndDate);
  if (user.subscriptionStatus === 'active' && (!end || end > now)) {
    totals.consultations = LEGACY_CONSULTATIONS[user.currentPlan] || 0;
    totals.since = toDate(user.subscriptionStartDate);
  }
  return totals;
}
